// src/components/Tutorial/Examples/ReviewForm.js
import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Avatar,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import { 
  ThumbUp as ThumbUpIcon, 
  ThumbDown as ThumbDownIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Palette as PaletteIcon,
  CalendarToday as CalendarTodayIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

const ReviewForm = () => {
  const theme = useTheme();
  
  return (
    <Box className="example-container" sx={{ mt: 3 }}>
      <Card sx={{ 
        maxWidth: 600,
        mx: 'auto',
        borderRadius: 4,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* Creator Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 2,
                  border: `4px solid ${theme.palette.primary.main}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }}
              >
                👤
              </Avatar>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Review Example Creator
              </Typography>
            </Box>

            {/* Recommendation */}
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom textAlign="center" sx={{ mb: 2 }}>
                Would you recommend this creator?
              </Typography>
              <ToggleButtonGroup
                value="yes"
                exclusive
                fullWidth
                sx={{
                  gap: 2,
                  '& .MuiToggleButtonGroup-grouped': {
                    flex: 1,
                    border: `3px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: '16px !important',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 3,
                    transition: 'all 0.3s ease-in-out',
                  },
                  '& .MuiToggleButtonGroup-grouped[value="yes"]': {
                    '&.Mui-selected': {
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      color: 'white',
                      borderColor: theme.palette.success.main,
                      boxShadow: '0 12px 32px rgba(76,175,80,0.4)',
                    },
                  },
                }}
              >
                <ToggleButton value="yes">
                  <ThumbUpIcon sx={{ mr: 1.5, fontSize: 28 }} /> Absolutely!
                </ToggleButton>
                <ToggleButton value="no">
                  <ThumbDownIcon sx={{ mr: 1.5, fontSize: 28 }} /> Not Really
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Rating Example - Content Quality */}
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <PaletteIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Content Quality
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How satisfied are you with the overall content?
              </Typography>
              <Stack direction="row" spacing={0.5} justifyContent="center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconButton key={star} sx={{ p: 0.5 }}>
                    {star <= 4 ? (
                      <StarIcon sx={{ fontSize: 36, color: theme.palette.warning.main }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 36, color: alpha(theme.palette.text.secondary, 0.3) }} />
                    )}
                  </IconButton>
                ))}
              </Stack>
            </Box>

            {/* Other Metrics Preview */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <CalendarTodayIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 0.5 }} />
                <Typography variant="caption" display="block">Frequency</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <VerifiedIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 0.5 }} />
                <Typography variant="caption" display="block">Authenticity</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TrendingUpIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 0.5 }} />
                <Typography variant="caption" display="block">Consistency</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <ChatIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 0.5 }} />
                <Typography variant="caption" display="block">Engagement</Typography>
              </Box>
            </Box>

            {/* Written Review */}
            <TextField
              label="Written Review (Optional)"
              multiline
              rows={4}
              placeholder="Share your honest experience..."
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {/* Submit Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Submit Review
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReviewForm;
